// Project ingress mode — HOW developerz reaches into a maintained repo.
//
//   `app`        — the GitHub App is installed on the repo; the bot acts through a
//                  server-minted installation token. The default SaaS path — no
//                  per-project GIT credentials ever live on the runner box, though
//                  the repo may still carry maintainer-authored `repo_env` secrets
//                  (arc 101 slice 06 — see `CredentialClass` below).
//   `credential` — no App (or a repo the App can't reach). The maintainer supplies
//                  their OWN credentials, held per-project in the runner's
//                  credential vault (`git`, `app_env`, `gateway_secrets`) —
//                  KEK-wrapped, never logged/tracked. The BYOVM credential path.
//
// snake_case identifiers: these cross REST bodies, the DB (`project_ingress`,
// `credential_vault.class`, #40) and the fleet wire verbatim — same convention as
// the other dispatch/fleet value objects (task-envelope, fleet-update).

import { z } from 'zod';
import { SshKeyWireMetaSchema } from './ssh-key';
import { TaskClass } from './task-envelope';

export const ProjectIngressMode = {
  App: 'app',
  Credential: 'credential',
} as const;
export const ProjectIngressModeSchema = z.enum(ProjectIngressMode);
export type ProjectIngressMode = z.infer<typeof ProjectIngressModeSchema>;

/**
 * The credential classes a repo stores for its runner, all riding ONE wire
 * transport ({@link SealedVaultEntrySchema}). Each is envelope-encrypted
 * (KEK-wrapped) at rest and never logged.
 *
 * - `git`             — clone/push/PR creds: an SSH key and/or a GitHub token.
 * - `app_env`         — the repo's runtime `.env` (values the app needs to build,
 *                       test and run — DB URLs, service keys), materialized at
 *                       repo-prep (#39).
 * - `gateway_secrets` — access config for a secrets gateway the runner fetches
 *                       from at repo-prep; the fetch itself is audited (#38).
 * - `repo_env`        — one maintainer-authored named secret from `repo_secrets`
 *                       (arc 101 slice 06), scoped to a {@link SecretEnvironment}.
 *                       The payload is the same `Record<string,string>` shape
 *                       `app_env` carries ({@link RepoEnvCredentialSchema}), so the
 *                       box merges what it unwraps instead of learning a second
 *                       delivery format. An `app`-mode repo carries it
 *                       ({@link ingressAllowsVaultClass}) — the default SaaS path
 *                       finally gets a `.env` without bringing its own git creds.
 * - `tool_service`    — the box env a CONNECTED tool service needs (arc 101 slice
 *                       07): one entry per `tool_service:<slug>` integration, the
 *                       payload being that descriptor's `env` map already rendered
 *                       to `{ BOX_VAR: value }` ({@link ToolServiceCredentialSchema}).
 *                       ACCOUNT-scoped, not repo-scoped — the human connects
 *                       ui-debugger once and every box running that account's work
 *                       gets the key. `sealed` ALWAYS: it is what the `.mcp.json`
 *                       `${VAR}` references resolve to in the coding child's process
 *                       env, and it may never become a file.
 * - `ssh_key`         — ONE customer-owned SSH key from `ssh_keys`, for reaching the
 *                       customer's OWN infrastructure (their bastion, their database
 *                       host, their deploy target). ACCOUNT/USER-scoped like
 *                       `tool_service` and deliberately reusable across repos; which
 *                       key an assignment carries is decided by matching the hosts
 *                       the repo declared in `.maintainer.yml` against the key's
 *                       `host_patterns` (`resolveSshKeysForHost`, `ssh-key.ts`),
 *                       never by anything the agent authors. NOT the same thing as
 *                       `git`, whose SSH key is repo-scoped and exists to clone and
 *                       push the maintained repo itself.
 *
 * The first three come from `credential_vault` (one row per class, per repo);
 * `repo_env` from `repo_secrets` (one row per `(repo_id, name, environment)`);
 * `tool_service` from `secrets` (the `integration:<slug>` vault row); `ssh_key` from
 * `ssh_keys` (one row per `(account_id, owner, name)`).
 */
export const CredentialClass = {
  Git: 'git',
  AppEnv: 'app_env',
  GatewaySecrets: 'gateway_secrets',
  RepoEnv: 'repo_env',
  ToolService: 'tool_service',
  SshKey: 'ssh_key',
} as const;
export const CredentialClassSchema = z.enum(CredentialClass);
export type CredentialClass = z.infer<typeof CredentialClassSchema>;

/** Every class in a stable order — for iteration and exhaustiveness. */
export const CREDENTIAL_CLASSES = [
  CredentialClass.Git,
  CredentialClass.AppEnv,
  CredentialClass.GatewaySecrets,
  CredentialClass.RepoEnv,
  CredentialClass.ToolService,
  CredentialClass.SshKey,
] as const;

/**
 * The classes the `credential_vault` table itself holds — the three that predate
 * `repo_env` and `tool_service`. That table has no `CHECK` on `class` (D3
 * no-pgEnum), so before those two existed `CredentialClassSchema` WAS its closed
 * vocabulary; this subset keeps it exactly as closed. A `repo_env` row belongs in
 * `repo_secrets` (it needs a `name` and an `environment` `credential_vault` has no
 * columns for) and a `tool_service` row in `secrets` (it is account-scoped, not
 * repo-scoped); one stored in the wrong table would ride the wire with the wrong
 * scoping fields and fail the box's own parse gate
 * ({@link SealedVaultEntrySchema}) — wedging a run over a write that should never
 * have been accepted.
 */
export const CredentialVaultClassSchema = z.enum([
  CredentialClass.Git,
  CredentialClass.AppEnv,
  CredentialClass.GatewaySecrets,
]);
export type CredentialVaultClass = z.infer<typeof CredentialVaultClassSchema>;

/**
 * Secret exposure tier — WHETHER a stored credential may ever exist as plaintext
 * outside the platform / runner process. Per credential, defaults to `sealed`.
 *
 * - `sealed` (default): the value is never written into the WORKSPACE, and a leak
 *   into an event/log is scrubbed by `@developerz/audit` redaction. LLM provider
 *   keys are ALWAYS sealed.
 *
 *   **`sealed` does NOT mean the agent cannot see it, and this comment used to say
 *   it did.** `apps/runner/src/credential/repo-env.ts` delivers a sealed
 *   `repo_env` secret into the CODING CHILD's process environment, where one
 *   `printenv` reads it; an `ssh_key` must exist as a file for `ssh(1)` to use it at
 *   all. Some sealed values are genuinely out of reach — the adapter env-profile
 *   (`apps/runner/src/backend/`) and a platform-proxied integration call keep theirs
 *   outside the agent process — but the tier does not promise it, so no feature may
 *   claim isolation on the strength of this word. What it promises is exactly two
 *   things: not materialized to the workspace, and redacted out of recorded output.
 *   Redaction is a substring match and will not catch a value the agent re-encodes
 *   before printing, so for a credential that reaches the customer's own estate the
 *   real mitigation is on their side — a deploy key, `from=` and `command=`
 *   restrictions in their `authorized_keys`.
 * - `materializable` (explicit opt-in): legitimately needed as plaintext on a dev
 *   box — a project `.env` to run the app, a test-account password. The runner may
 *   write it into the workspace `.env` (`apps/runner/src/credential/materialize-env.ts`),
 *   gated on this flag AND a `dev` environment, auditing each materialization
 *   (secret NAME, never the value).
 */
export const CredentialExposure = {
  Sealed: 'sealed',
  Materializable: 'materializable',
} as const;
export const CredentialExposureSchema = z.enum(CredentialExposure);
export type CredentialExposure = z.infer<typeof CredentialExposureSchema>;

/** Sealed-by-default: the tier a credential takes when none is specified. */
export const DEFAULT_CREDENTIAL_EXPOSURE: CredentialExposure = CredentialExposure.Sealed;

/**
 * Only the two `.env` classes may be `materializable` — `app_env` (a whole file,
 * one `credential_vault` row) and `repo_env` (one named secret, one `repo_secrets`
 * row). They ARE the app's runtime environment, the only values the runner writes
 * to plaintext on a dev box. `git` and `gateway_secrets` are consumed entirely
 * inside the platform/runner process (clone/push, gateway fetch), so flagging them
 * materializable is a category error the seal path rejects. Encoded here so server
 * (seal) and runner (materialize) agree on which class may ever leave sealed
 * storage.
 *
 * Class permission is necessary, never sufficient: a `repo_env` secret must ALSO
 * clear {@link environmentAllowsMaterializable}, because a `prod` credential must
 * never reach a box's disk however its class is flagged.
 */
export function classAllowsMaterializable(cls: CredentialClass): boolean {
  return cls === CredentialClass.AppEnv || cls === CredentialClass.RepoEnv;
}

/**
 * Only `credential`-mode projects need the maintainer's OWN credentials on the box;
 * `app` mode clones with a server-minted installation token. Deliberately UNCHANGED
 * by arc 101 slice 06: `repo_env` widened what a vault may CARRY, not what a mode
 * REQUIRES. An `app`-mode repo qualifies to carry `repo_env` entries
 * ({@link ingressAllowsVaultClass}) but has no `credential_vault` row to read, so
 * this stays the cheap gate in front of that read rather than a per-claim cost the
 * default SaaS path pays forever. Encoded here so both the server (provisioning)
 * and the runner (repo-prep) agree on when a vault must exist.
 */
export function ingressRequiresVault(mode: ProjectIngressMode): boolean {
  return mode === ProjectIngressMode.Credential;
}

/**
 * mode → the classes a repo in it may legitimately carry. A `Record` over the mode
 * union, so a third ingress mode cannot be added without answering this question.
 */
const VAULT_CLASSES_BY_INGRESS: Record<ProjectIngressMode, readonly CredentialClass[]> = {
  [ProjectIngressMode.App]: [
    CredentialClass.RepoEnv,
    CredentialClass.ToolService,
    CredentialClass.SshKey,
  ],
  [ProjectIngressMode.Credential]: CREDENTIAL_CLASSES,
};

/**
 * Whether this ingress mode may carry that vault class. `repo_env` (a
 * maintainer-authored `.env`) and `tool_service` (a connected service's box env) are
 * the two classes an `app`-mode repo qualifies for: both are orthogonal to how we
 * authenticate to the repo — one is the app's own runtime environment, the other is
 * ACCOUNT-level and not about this repo at all — so the default SaaS path carries
 * them — as does `ssh_key`, which is account-scoped for the same reason and is about
 * the CUSTOMER's own hosts rather than about how we authenticate to the repo — while
 * a `git` / `app_env` / `gateway_secrets` entry on an app-mode assignment stays what
 * it always was — a category error (that repo's toolchain authenticates with an
 * installation token minted per run, and nothing on the box should hold a credential
 * outliving it). A `credential`-mode repo may carry every class: bringing your own
 * git creds does not stop you storing platform secrets.
 *
 * Encoded here so the API (write), the assigner (seal) and the box (parse) read one
 * table instead of three copies of the rule.
 */
export function ingressAllowsVaultClass(mode: ProjectIngressMode, cls: CredentialClass): boolean {
  return VAULT_CLASSES_BY_INGRESS[mode].includes(cls);
}

/**
 * WHICH environment a stored repo secret belongs to (arc 101 slice 06,
 * `repo_secrets`). Part of the row's IDENTITY, not a label: uniqueness is
 * `(repo_id, name, environment)`, so one repo holds a `DATABASE_URL` per
 * environment and an assignment is handed exactly the set matching its own
 * environment — never a merge, never a silent downgrade to a laxer one.
 *
 * - `dev`  — the box's own throwaway workspace: the only environment where a
 *   `materializable` secret may ever be written to `<checkout>/.env`.
 * - `ci`   — values a CI/verify run needs; delivered as process env only.
 * - `prod` — real production credentials. Delivered as sealed adapter env or
 *   REFUSED for that assignment; never materialized to disk anywhere.
 */
export const SecretEnvironment = {
  Dev: 'dev',
  Ci: 'ci',
  Prod: 'prod',
} as const;
export const SecretEnvironmentSchema = z.enum(SecretEnvironment);
export type SecretEnvironment = z.infer<typeof SecretEnvironmentSchema>;

/** All three environments in a stable order — for iteration and exhaustiveness. */
export const SECRET_ENVIRONMENTS = [
  SecretEnvironment.Dev,
  SecretEnvironment.Ci,
  SecretEnvironment.Prod,
] as const;

/**
 * `materializable` is only ever legitimate in `dev` — the throwaway workspace
 * where the app must actually run. A `ci`/`prod` secret is consumed inside the
 * runner process (adapter env) or not at all; writing one to a file on a box we
 * do not operate is the leak this predicate exists to stop. The twin of
 * {@link classAllowsMaterializable}, encoded here so the API (write), the
 * assigner (seal) and the runner (materialize) agree on one rule — the
 * `repo_secrets_exposure_env_ck` check constraint is its DB-side backstop.
 */
export function environmentAllowsMaterializable(env: SecretEnvironment): boolean {
  return env === SecretEnvironment.Dev;
}

/**
 * task class → the ONE stored environment an assignment of that class is handed
 * (arc 101 slice 06 step 4 — the assigner's seal). A `Record` over the whole
 * {@link TaskClass} union, so a fifth class cannot be added without answering the
 * question: guessing an environment is the exact failure this slice exists to stop,
 * and a `Partial` would answer it by silently shipping nothing.
 */
const SECRET_ENVIRONMENT_BY_TASK_CLASS: Record<TaskClass, SecretEnvironment> = {
  // A verify / CI run consumes the values that lane needs — process env only.
  [TaskClass.Ci]: SecretEnvironment.Ci,
  // Everything else runs in the box's own throwaway checkout: `dev`, the one
  // environment where a `materializable` secret may reach `<checkout>/.env`.
  [TaskClass.Interactive]: SecretEnvironment.Dev,
  [TaskClass.Coding]: SecretEnvironment.Dev,
  [TaskClass.Gardener]: SecretEnvironment.Dev,
};

/**
 * WHICH environment's stored secrets an assignment of this scheduling class may
 * carry — the environment half of the assigner's seal filter (the other half is the
 * repo). A MATCH, never a merge and never a fallback to a laxer set: a `ci`
 * assignment is handed the `ci` rows or none, so a repo that stored only `dev`
 * values does not silently leak them into a CI lane.
 *
 * **Nothing maps to `prod`, and that absence IS the policy.** No task class can be
 * scheduled into reading a production credential, so `prod` rows are stored and
 * never sealed onto a fleet assignment — the "refused, never silently downgraded"
 * half of the doc-06 rule, encoded as a missing branch rather than a runtime check
 * someone can widen by accident. Delivering `prod` values is a deploy/ops surface
 * this ladder deliberately does not open.
 *
 * An unrecognized class degrades to `dev` — the same neutral default
 * `taskClassOf` supplies for an envelope with no class at all, and the least
 * privileged of the three sets.
 */
export function secretEnvironmentForTaskClass(taskClass: TaskClass): SecretEnvironment {
  return SECRET_ENVIRONMENT_BY_TASK_CLASS[taskClass] ?? SecretEnvironment.Dev;
}

/**
 * The environments a stored secret can ever REACH a box in — every value some
 * task class maps to, and nothing else.
 *
 * DERIVED from {@link SECRET_ENVIRONMENT_BY_TASK_CLASS} rather than written out,
 * because the interesting half of this set is what is MISSING from it: `prod` is
 * absent exactly because no task class maps to it, and a hand-written
 * `['dev','ci']` would be a second place that fact lives — one that keeps saying
 * `prod` is undeliverable after somebody adds a class that delivers it. The
 * `Record` is total over {@link TaskClass}, so this enumerates the map, not a
 * sample of it.
 *
 * Read by `next_steps`' `repo_secrets` rung to tell a maintainer that the secrets
 * they stored will never be handed to any lane — the silent misconfiguration a
 * `prod`-only repo has today, where the values are stored, sealed, and byte-for-byte
 * unreachable.
 */
export const DELIVERABLE_SECRET_ENVIRONMENTS: readonly SecretEnvironment[] =
  SECRET_ENVIRONMENTS.filter((env) =>
    Object.values(SECRET_ENVIRONMENT_BY_TASK_CLASS).includes(env),
  );

/**
 * May a secret be STORED for this environment at all? The write-seam twin of the
 * set above, and the reason it exists: storing a `prod` secret used to answer 200
 * and then produce a row no lane could ever be handed — undeliverable forever,
 * announced only by a `next_steps` rung the maintainer may never read. Invalid
 * config throws; it does not silently degrade, so the API refuses the write where
 * the value enters instead of accepting it and hoping somebody notices.
 *
 * The refusal lives on the DERIVED set, so the day a task class maps to `prod` the
 * write starts being accepted by the same edit that makes it deliverable. The
 * stored vocabulary keeps `prod` (`repo_secrets_environment_ck`, and rows written
 * before this gate must still parse) — this is about what may be written NEXT.
 */
export function environmentIsDeliverable(env: SecretEnvironment): boolean {
  return DELIVERABLE_SECRET_ENVIRONMENTS.includes(env);
}

// ─── per-class plaintext payloads (#292) ─────────────────────────────────────
//
// The shapes a credential holds BEFORE sealing / after unwrapping. Shared by the
// seal side (`scripts/light-switch.ts` validates before `encryptDek`) and the
// consume side (`apps/runner/src/credential/vault.ts` parses after `decryptDek`)
// so a malformed credential is rejected at write time, never surfacing as a
// decode failure at repo-prep. Pure Zod — the crypto stays in `@developerz/config`.

/** `git` class — at least one of an SSH private key or a GitHub token. */
export const GitCredentialSchema = z
  .object({
    ssh_key: z.string().min(1).optional(),
    gh_token: z.string().min(1).optional(),
  })
  .refine((c) => c.ssh_key !== undefined || c.gh_token !== undefined, {
    message: 'git credential requires an ssh_key or a gh_token',
  });
export type GitCredential = z.infer<typeof GitCredentialSchema>;

/** `app_env` class — the repo's runtime `.env` map (materialized at repo-prep, #39). */
export const AppEnvCredentialSchema = z.record(z.string().min(1), z.string());
export type AppEnvCredential = z.infer<typeof AppEnvCredentialSchema>;

/** `gateway_secrets` class — access config for a secrets gateway we fetch from. */
export const GatewaySecretsCredentialSchema = z.object({
  endpoint: z.url(),
  token: z.string().min(1),
});
export type GatewaySecretsCredential = z.infer<typeof GatewaySecretsCredentialSchema>;

/** Longest stored secret name — matches the `repo_secrets_name_ck` length bound. */
export const REPO_SECRET_NAME_MAX = 128;

/**
 * A stored secret's NAME — the app-side twin of the `repo_secrets_name_ck` check
 * constraint (`packages/db/src/schema.ts`), same regex, same length bound. The name
 * is written VERBATIM into `<checkout>/.env` and into the coding container's
 * `--env <NAME>` allowlist, so anything that is not a POSIX env-var name corrupts
 * both surfaces (an `=`, a space or a newline in it is an injection, not a typo).
 */
export const RepoSecretNameSchema = z
  .string()
  .min(1)
  .max(REPO_SECRET_NAME_MAX)
  .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'must be a POSIX env-var name');

/**
 * `repo_env` class — `NAME → value` pairs from `repo_secrets`, the SAME
 * `Record<string,string>` shape `app_env` carries, keyed by the constrained name
 * above. A map rather than a bare value ON PURPOSE: `repo_secrets` seals one
 * ciphertext per `(repo_id, name, environment)` and the assigner never decrypts, so
 * it maps each row to its own wire entry and the box merges what it unwraps. One
 * transport, one payload shape — whether a class holds the whole file (`app_env`,
 * one blob) or a single name (`repo_env`, one blob each).
 */
export const RepoEnvCredentialSchema = z.record(RepoSecretNameSchema, z.string());
export type RepoEnvCredential = z.infer<typeof RepoEnvCredentialSchema>;

/**
 * `tool_service` class (arc 101 slice 07) — ONE connected tool service's BOX ENV,
 * already rendered: `{ UI_DEBUGGER_API_KEY: '…', UI_DEBUGGER_BASE_URL: '…' }`. The
 * same `Record<string,string>` shape as the two `.env` classes, keyed by the same
 * POSIX env-var name, because it lands in the same place — the coding child's
 * process env, which is what the `.mcp.json` `${VAR}` references resolve against.
 *
 * The render happens ONCE, at connect time (`apps/api` `/v1/integrations`), through
 * the descriptor's own `env` map (`toolServiceBoxEnv`) — so the ciphertext the vault
 * stores IS the box env, the assigner forwards a blob it never decrypts, and the box
 * merges what it unwraps without needing to know which service it came from. The
 * VAR↔value binding is AEAD-authenticated inside the envelope, exactly like the
 * NAME↔value binding of a `repo_env` row.
 */
export const ToolServiceCredentialSchema = z.record(RepoSecretNameSchema, z.string());
export type ToolServiceCredential = z.infer<typeof ToolServiceCredentialSchema>;

// ─── the sealed vault entry (wire, #292) ─────────────────────────────────────

/**
 * Hard cap on one assignment's vault array (`FleetTaskAssignment.vault`). Sized for
 * a real `.env`: the three `credential_vault` classes plus ~60 `repo_secrets` names,
 * each its own sealed entry. A repo needing more secrets than that has outgrown a
 * pasted `.env` and wants the `gateway_secrets` class (the runner fetches from the
 * maintainer's own gateway) — the cap is what keeps one NATS message and one outbox
 * row from becoming an unbounded secret dump.
 */
export const VAULT_MAX_ENTRIES = 64;

/**
 * One vault credential as it rides `FleetTaskAssignment.vault` — the ONE delivery
 * transport for every class, `credential`-mode ingress and `repo_env` alike. `blob`
 * is base64 of the row's ciphertext (`credential_vault` or `repo_secrets`)
 * RE-TARGETED at the claiming box (#1300): the DEK layer is re-wrapped under that
 * box's derived subkey, the data ciphertext is carried through untouched. So the
 * assigner still never decrypts (no plaintext exists server-side), the outbox row
 * that carries it is ciphertext at rest, and it unwraps ONLY on the box it was
 * claimed for — the exact `llm_credential.sealed_key` posture. snake_case: crosses
 * NATS verbatim.
 *
 * Four invariants are enforced at this parse gate, because the box's own parse is
 * the last thing standing between a malformed assignment and a plaintext secret on
 * a disk we do not operate:
 *
 * 1. a `repo_env` entry must NAME its environment — without it the box cannot tell
 *    a dev value it may write to `.env` from a production credential it may not,
 *    and defaulting either way is the failure mode;
 * 2. a `tool_service` entry must NOT name one — it is ACCOUNT-scoped (one connected
 *    service, every box that account's work reaches), so an environment on it is a
 *    scope nobody wrote and nothing enforces. Refused rather than ignored: silently
 *    dropping the field would make a mothership that thinks it scoped a key look
 *    exactly like one that never tried;
 * 3. `materializable` only on a class that allows it ({@link classAllowsMaterializable})
 *    — which `tool_service` never does, so a service key can never become a file;
 * 4. `materializable` only in `dev` ({@link environmentAllowsMaterializable}) — the
 *    wire twin of the `repo_secrets_exposure_env_ck` constraint;
 * 5. an `ssh_key` entry must carry its `ssh` metadata and no environment, and no
 *    other class may carry that metadata. The box builds the run's `ssh_config` and
 *    `UserKnownHostsFile` out of it, so an entry missing it is a key with no host to
 *    offer itself to and no pin to verify against — the two facts whose absence the
 *    box must never paper over by falling back to the ambient ssh configuration.
 */
export const SealedVaultEntrySchema = z
  .object({
    class: CredentialClassSchema,
    /**
     * Sealed-by-default: an entry from a mothership that predates the tier degrades
     * to `sealed` rather than failing the whole assignment to parse (a rejected
     * assignment wedges the run; a sealed secret merely stays off disk).
     */
    exposure: CredentialExposureSchema.default(DEFAULT_CREDENTIAL_EXPOSURE),
    /** base64(KEK-wrapped ciphertext). Unwrapped ONLY on the box, with the box KEK. */
    blob: z.string().min(1),
    /**
     * Which environment this secret belongs to — REQUIRED for `repo_env` (part of
     * the row's identity), REFUSED for `tool_service` (account-scoped, no
     * environment exists), absent for the `credential_vault` classes, which carry no
     * environment column and stay gated box-side on `DZ_BOX_ENVIRONMENT`.
     */
    environment: SecretEnvironmentSchema.optional(),
    /**
     * `ssh_key` only — the PUBLIC half of the key: its name, fingerprint, the host
     * patterns it is offered for and the host keys pinned at registration. Not a
     * secret, and decided entirely platform-side before the agent starts.
     */
    ssh: SshKeyWireMetaSchema.optional(),
  })
  .superRefine((entry, ctx) => {
    if (entry.class === CredentialClass.SshKey) {
      if (entry.ssh === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'an ssh_key entry must carry its host patterns and pinned host keys',
          path: ['ssh'],
        });
      }
      if (entry.environment !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'an ssh_key entry is account-scoped and carries no environment',
          path: ['environment'],
        });
      }
    } else if (entry.ssh !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `a '${entry.class}' credential carries no ssh metadata`,
        path: ['ssh'],
      });
    }
    if (entry.class === CredentialClass.RepoEnv && entry.environment === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a repo_env entry must name its environment (dev | ci | prod)',
        path: ['environment'],
      });
    }
    if (entry.class === CredentialClass.ToolService && entry.environment !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a tool_service entry is account-scoped and carries no environment',
        path: ['environment'],
      });
    }
    if (entry.exposure !== CredentialExposure.Materializable) return;
    if (!classAllowsMaterializable(entry.class)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `a '${entry.class}' credential may never be materializable`,
        path: ['exposure'],
      });
    }
    if (entry.environment !== undefined && !environmentAllowsMaterializable(entry.environment)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `a materializable secret requires environment dev, not '${entry.environment}'`,
        path: ['exposure'],
      });
    }
  });
export type SealedVaultEntry = z.infer<typeof SealedVaultEntrySchema>;
